// Paywall dialog (COL-352) — matches Neel's mockup (Slack #tech-ops #10): a
// card with a lock, a trigger-specific "Subscribe to …" heading + body, and
// Subscribe (→ /admin/billing) + Back to overview actions. Copy map is shared
// with the full-page EntitlementRouteGate via paywallCopy().
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Lock, Check } from "lucide-react";

export type PaywallTrigger =
  | "create_tenant"
  | "create_owner"
  | "create_property"
  | "create_template"
  // Future triggers from the rollout notes — typed now so wiring them later
  // is a one-line guardCreate call:
  | "act_as"
  | "virtual_tour"
  | "mietvertrag"
  | "verwaltervertrag";

const COPY: Record<PaywallTrigger, { title: string; body: string }> = {
  create_tenant: {
    title: "Subscribe to add tenants",
    body: "Creating tenants is part of the paid plan. Subscribe once and manage your tenants, contracts and payments end to end. Tip: set up your first property before adding tenants.",
  },
  create_owner: {
    title: "Subscribe to add owners",
    body: "Owner records — including bank details and representatives — are part of the paid plan. Subscribe once and manage every owner relationship in one place.",
  },
  create_property: {
    title: "Subscribe to add properties",
    body: "Your properties are the heart of the platform. Subscribe once and set up properties, apartments and rooms with your own revenue terms.",
  },
  create_template: {
    title: "Subscribe to upload templates",
    body: "Bring your own contract templates and generate documents with your branding. Template uploads are part of the paid plan.",
  },
  act_as: {
    title: "Act-as is a premium feature",
    body: "Viewing the platform through a tenant's or owner's eyes is part of the paid plan.",
  },
  virtual_tour: {
    title: "Virtual tours are a premium feature",
    body: "Attach virtual tour links to your rooms with the paid plan.",
  },
  mietvertrag: {
    title: "The Mietvertrag is a premium feature",
    body: "Generate ready-to-sign rental contracts with the paid plan.",
  },
  verwaltervertrag: {
    title: "The Verwaltervertrag is a premium feature",
    body: "Generate property management agreements with the paid plan.",
  },
};

export function paywallCopy(trigger: PaywallTrigger): {
  title: string;
  body: string;
} {
  return COPY[trigger];
}

// What one subscription unlocks — shown on both paywall surfaces so an operator
// sees WHY to subscribe, not just a locked door. Trigger-agnostic on purpose:
// the plan is one price that turns on the whole workspace.
export const PAYWALL_HIGHLIGHTS: string[] = [
  "Set up properties, apartments and rooms",
  "Add tenants and owners",
  "Generate contracts from your own templates",
  "Your company's branding on tenant emails — not coliviq's",
];

export function PaywallDialog({
  open,
  onOpenChange,
  trigger,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: PaywallTrigger;
}) {
  const navigate = useNavigate();
  const { title, body } = paywallCopy(trigger);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-4 w-4" aria-hidden />
            {title}
          </DialogTitle>
          <DialogDescription>{body}</DialogDescription>
        </DialogHeader>
        {/* What the subscription unlocks — same checklist as the full-page
            EntitlementRouteGate, so both paywall surfaces read the same. */}
        <div className="rounded-lg border bg-muted/30 p-4">
          <p className="mb-3 text-sm font-medium text-foreground">
            One subscription unlocks
          </p>
          <ul className="space-y-2.5">
            {PAYWALL_HIGHLIGHTS.map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-sm">
                <Check
                  className="mt-0.5 h-4 w-4 flex-none text-primary"
                  aria-hidden
                />
                <span className="text-muted-foreground">{item}</span>
              </li>
            ))}
          </ul>
        </div>
        {/* Subscribe (primary) + Back to overview (ghost), matching Neel's
            paywall mockup and the full-page EntitlementRouteGate. */}
        <DialogFooter className="flex-row justify-start gap-3 sm:justify-start">
          <Button
            onClick={() => {
              onOpenChange(false);
              navigate("/admin/billing");
            }}
          >
            Subscribe
          </Button>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Back to overview
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
